import React from 'react';
import {
  Redirect,
  Route,
  RouteComponentProps,
  withRouter,
} from 'react-router-dom';

import './Frame.less';
import HwzComponent from '../common/component/HwzComponent';
import UserLogin from './user/Login';
import UserRegister from './user/Register';
import OnlineUserList from './user/OnlineUserList'; 
import UserList from './user/List';
import DeptList from './department/List';
import MobileUserList from './user/MobileList';
import UserDetail from './user/Detail';
import UserEdit from './user/Edit';
import UserAuthentication from './user/Authentication';

import PreferenceIndex from './preference/Index';
import PreferenceEdit from './preference/Edit';
import PreferenceEngineEdit from './preference/PreviewEngineEdit';
import PreferenceScanEdit from './preference/ScanEdit';
import FilesLogList from './fileslog/Listview';
import SystemLogList from './syslog/SystemLogList';
import DashboardIndex from './dashboard/Index';
import InstallIndex from './install/Index';
import MatterList from './matter/List';
import MatterDetail from './matter/Detail';

import BinList from './bin/List';

import ShareList from './share/List';
import ShareDetail from './share/Detail';
import User from '../common/model/user/User';
import Moon from '../common/model/global/Moon';
import Sun from '../common/model/global/Sun';
import FrameLoading from './widget/FrameLoading';
import Preference from '../common/model/preference/Preference';
import { WebResultCode } from '../common/model/base/WebResultCode';
import MessageBoxUtil from '../common/util/MessageBoxUtil';
import BottomLayout from './layout/BottomLayout';
import SideLayout from './layout/SideLayout';
import TopLayout from './layout/TopLayout';
import ContentLayout from './layout/ContentLayout';
import HaStatus from '../common/model/ha/HaStatus';
import Lang from '../common/model/global/Lang';
import { Alert, Progress } from 'antd';

import SpaceList from './space/List';
import SpaceMemberList from './space/member/List';
import SpaceMatterList from './space/matter/List';

import RemovableFileBrowser from './removable/browser/RemovableFileBrowser';
import RemovableLogs from './removable/logs/RemovableLogs';

import withAutoLogout from '../common/util/withAutoLogout';

// 声明全局变量，用于在 HttpBase 中识别分享页面
declare global {
  interface Window {
    __IS_SHARE_PAGE__: boolean;
  }
}

interface IProps extends RouteComponentProps<{}> {}

interface IState {
  isRedirecting: boolean;
}

class RawFrame extends HwzComponent<IProps, IState> {
  user: User = Moon.getSingleton().user;
  preference: Preference = Moon.getSingleton().preference;
  initialized: boolean = false;
  private redirectChecked: boolean = false; // 防止重复重定向
  haStatus: HaStatus = new HaStatus(this);
  private haStatusTimer: any = null;

  constructor(props: IProps) {
    super(props);
    this.state = {
      isRedirecting: false,
    };
  }

  componentDidMount() {
    window.__IS_SHARE_PAGE__ = this.props.location.pathname.startsWith('/share/detail');
    Sun.getSingleton().reactRouter = this.props.history;
    Sun.getSingleton().frameComponent = this;
    this.initialize();
    this.startHaStatusPolling();
    // 注册上传状态回调，List 更新 Sun 时立即触发浮窗刷新
  }

  componentDidUpdate(prevProps: IProps) {
    // 路由变化时检查是否需要重定向
    if (this.props.location.pathname !== prevProps.location.pathname) {
      this.checkAndHandleRedirect();
    }
  }

  initialize() {
    let that = this;
    let pathname: string = that.props.location.pathname;

    this.preference.httpFetch(
      function () {
        // 白名单：完全不要求登录，也不尝试登录
        let whitePaths = [
          '/user/login',
          '/user/register',
          '/user/authentication',
          '/install/index', // 安装页面也加入白名单
        ];

        // 分享页面：不要求登录，也不尝试登录
        let sharePaths = ['/share/detail'];

        if (whitePaths.findIndex((path) => pathname.startsWith(path)) >= 0) {
          that.initialized = true;
          that.updateUI();
          that.refreshHaStatus();
        } else if (sharePaths.findIndex((path) => pathname.startsWith(path)) >= 0) {
          // 分享页面直接渲染，不调用 httpInfo
          that.initialized = true;
          that.updateUI();
          that.refreshHaStatus();
        } else {
          // 其他页面：必须登录，如果未登录会跳转
          that.user.httpInfo(true, function () {
            that.initialized = true;
            that.updateUI();
            // 获取用户信息后检查重定向
            that.checkAndHandleRedirect();
            that.refreshHaStatus();
          });
        }
      },
      function (errMessage: string, response: any) {
        that.initialized = true;
        that.updateUI();
        if (
          response &&
          response.data &&
          response.data['code'] === WebResultCode.NOT_INSTALLED
        ) {
          MessageBoxUtil.warning('系统尚未安装，即将引导进入安装页面！');
          that.preference.installed = false;
          that.props.history.push('/install/index');
        }
      }
    );
  }

  startHaStatusPolling() {
    this.refreshHaStatus();
    this.haStatusTimer = setInterval(() => this.refreshHaStatus(), 30000);
  }

  refreshHaStatus() {
    if (!this.initialized || !this.preference.installed) return;

    this.haStatus.httpStatus((data: any) => {
      if (data) this.haStatus.assign(data);
      this.updateUI();
    });
  }

  /**
   * 检查并处理重定向逻辑
   * 当 ctrlMod === 3 时，所有非白名单路径都应该重定向到 /removable/list
   */
  checkAndHandleRedirect() {
    // 防止重复检查和重定向
    if (this.state.isRedirecting || this.redirectChecked) {
      return;
    }

    const pathname = this.props.location.pathname;
    
    // 不需要重定向的白名单路径
    const whitelistPaths = [
      '/user/login',
      '/user/register',
      '/user/authentication',
      '/install/index',
      '/share/detail',
      '/removable/list',
      '/removable/browse',
      '/removable/logs',
    ];

    // 检查是否是白名单路径
    const isWhitelistPath = whitelistPaths.some(path => pathname.startsWith(path));
    
    // 检查是否是分享页面
    const isSharePage = pathname.startsWith('/share/detail');
    
    // 检查用户是否已登录
    const isUserLoggedIn = this.user && this.user.uuid;
    
    // 检查是否是安装页面
    const isInstallPage = pathname.startsWith('/install/index');
    
    // 检查系统是否已安装
    const isSystemInstalled = this.preference.installed;

    // 不需要重定向的情况
    if (
      !isSystemInstalled ||  // 系统未安装
      !isUserLoggedIn ||     // 用户未登录
      isWhitelistPath ||     // 白名单路径
      isSharePage ||         // 分享页面
      isInstallPage          // 安装页面
    ) {
      this.redirectChecked = true;
      return;
    }

    // 检查是否需要重定向
    const ctrlMod = this.preference.ctrlMod || 0;
    
    if (ctrlMod === 3 && pathname !== '/removable/list') {
      // 检查是否已经是目标路径，防止循环重定向
      if (!pathname.startsWith('/removable/list')) {
        console.log(`检测到 ctrlMod=3，重定向到可移动存储页面: 当前路径=${pathname}`);
        
        this.setState({ isRedirecting: true });
        this.redirectChecked = true;
        
        // 使用 setTimeout 避免在渲染过程中更新状态
        setTimeout(() => {
          this.props.history.replace('/removable/list');
          this.setState({ isRedirecting: false });
        }, 0);
      }
    } else {
      this.redirectChecked = true;
    }
  }

  render() {
    let content: React.ReactNode;
    const pathname = this.props.location.pathname;
    const isSharePage = pathname.startsWith('/share/detail');
    const isInstallPage = pathname.startsWith('/install/index');

    // 设置全局变量，供 HttpBase 中的 specialErrorHandler 使用
    window.__IS_SHARE_PAGE__ = isSharePage;

    if (this.initialized) {
      // 检查是否需要重定向
      this.checkAndHandleRedirect();

      content = (
        <div className="pages-frame-inner">
          {/* 分享页面和安装页面都不显示侧边栏和顶部栏 */}
          {!isSharePage && !isInstallPage && <SideLayout />}
          {!isSharePage && !isInstallPage && <TopLayout />}
          <ContentLayout>
            {this.preference.installed ? (
              <div className="pages-content">
                {/* 根据 ctrlMod 设置默认重定向 */}
                {this.preference.ctrlMod === 3 ? (
                  <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/removable/list" />}
                  />
                ) : (
                  <Route
                    exact
                    path="/"
                    render={() => <Redirect to="/matter/list" />}
                  />
                )}
                
                <Route path="/user/login" component={UserLogin} />
                <Route path="/user/register" component={UserRegister} />
                <Route path="/user/detail/:uuid" component={UserDetail} />
                <Route path="/user/list" component={UserList} />
                <Route path="/mobile/user/list" component={MobileUserList} />
                <Route path="/user/create" component={UserEdit} />
                <Route path="/user/edit/:uuid" component={UserEdit} />

                <Route path="/department/list" component={DeptList} />
                <Route
                  path="/user/authentication/:authentication"
                  component={UserAuthentication}
                />

                <Route path="/dashboard/index" component={DashboardIndex} />
                <Route path="/fileslog/Listview" component={FilesLogList} />
                <Route path="/syslog/SystemLogList" component={SystemLogList} />
                <Route path="/preference/index" component={PreferenceIndex} />
                <Route path="/preference/edit" component={PreferenceEdit} />
                <Route
                  path="/preference/engine/edit"
                  component={PreferenceEngineEdit}
                />
                <Route
                  path="/preference/scan/edit"
                  component={PreferenceScanEdit}
                />

                <Route path="/matter/detail/:uuid" component={MatterDetail} />
                <Route
                  exact
                  path="/matter"
                  render={() => <Redirect to="/matter/list" />}
                />
                <Route path="/matter/list" component={MatterList} />
                <Route path="/user/online/list" component={OnlineUserList} />
                <Route path="/share/list" component={ShareList} />
                <Route path="/share/detail/:uuid" component={ShareDetail} />

                <Route path="/bin/list" component={BinList} />
                <Route path="/space" exact component={SpaceList} />
                <Route
                  path="/space/:spaceUuid/member"
                  exact
                  component={SpaceMemberList}
                />
                <Route
                  path="/space/:spaceUuid/matter/list"
                  exact
                  component={SpaceMatterList}
                />
                <Route
                  path="/space/:spaceUuid/matter/detail/:uuid"
                  exact
                  component={MatterDetail}
                />
                
                <Route path="/removable/list" exact component={RemovableFileBrowser} />
                <Route path="/removable/browse/:deviceId" exact component={RemovableFileBrowser} />
                <Route path="/removable/logs" exact component={RemovableLogs} />
              </div>
            ) : (
              <div className="pages-content">
                <Route path="/install/index" component={InstallIndex} />
              </div>
            )}
          </ContentLayout>
          {!isSharePage && !isInstallPage && <BottomLayout />}
        </div>
      );
    } else {
      content = <FrameLoading />;
    }

    const showHaBanner = !isSharePage && !isInstallPage && this.haStatus.takeover;

    return (
      <div className={`pages-frame${showHaBanner ? ' ha-takeover-alert' : ''}`}>
        {showHaBanner && (
          <Alert
            className="ha-takeover-banner"
            banner
            type="error"
            showIcon
            message={this.haStatus.message || Lang.t('haTakeover')}
            style={{ fontWeight: 'bold' }}
          />
        )}
        {content}

        <UploadFloatingWidget />
      </div>
    );
  }
}

const Frame = withRouter<IProps, React.ComponentType<IProps>>(RawFrame);
export default withAutoLogout(Frame);

// 独立上传浮窗组件：自带轮询，不触发 Frame 重渲染
class UploadFloatingWidget extends React.Component<{}, { tick: number }> {
  private timer: any = null;
  constructor(props: {}) {
    super(props);
    this.state = { tick: 0 };
  }
  componentDidMount() {
    this.timer = setInterval(() => {
      const sun = Sun.getSingleton();
      if (sun.dirUploadPhase) {
        this.setState({ tick: this.state.tick + 1 });
      }
    }, 500);
  }
  componentWillUnmount() {
    if (this.timer) clearInterval(this.timer);
  }
  render() {
    const sun = Sun.getSingleton();
    if (!sun.dirUploadPhase || sun.dirUploadPhase === 'done') return null;
    return (
      <div style={{
        position: 'fixed', bottom: 20, right: 20, zIndex: 9999,
        background: '#fff', borderRadius: 8, padding: '12px 16px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: 220, maxWidth: 320,
        border: '1px solid #e1e4e8'
      }}>
        <div style={{ fontWeight: 500, marginBottom: 6, fontSize: 13 }}>
          {sun.dirUploadPhase === 'scanning' && Lang.t('matter.scanning')}
          {sun.dirUploadPhase === 'preparing' && Lang.t('matter.preparing')}
          {sun.dirUploadPhase === 'creating_dirs' && Lang.t('matter.creatingDirs')}
          {sun.dirUploadPhase === 'uploading' && `${Lang.t('matter.uploading')}: ${sun.dirUploadDone}${sun.dirUploadTotal > 0 ? '/' + sun.dirUploadTotal : ''}`}
        </div>
        {sun.dirUploadPhase === 'scanning' ? (
          <Progress percent={100} status="success" strokeColor="#108ee9" showInfo={false} size="small" />
        ) : (
          <Progress
            percent={sun.dirUploadTotal > 0 ? Math.round((sun.dirUploadDone / sun.dirUploadTotal) * 100) : 0}
            status="active"
            strokeColor={{ from: '#108ee9', to: '#87d068' }}
            size="small"
          />
        )}
        {sun.dirUploadPhase === 'uploading' && sun.dirUploadCurrentFile && (
          <div style={{ marginTop: 4, fontSize: 11, color: '#666', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {sun.dirUploadCurrentFile}
          </div>
        )}
        <div style={{ marginTop: 6 }}>
          <a onClick={() => { sun.dirUploadAborted = true; }} style={{ color: '#ff4d4f', fontSize: 12 }}>
            {Lang.t('common.cancel', '取消')}
          </a>
        </div>
      </div>
    );
  }
}
